import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { camelToDisplay, capitalize, isOnDemandProgrammeConstructorProps, NodeEdgeMapper, OnDemandProgramme } from '@iotv/datamodel-core';
import { AppValueTypes, getObjectHash } from '@iotv/iotv-v3-types';
import { deviceEnabledTypes, isDeviceEnabledType } from '@iotv/payload-definition';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Card, CardContent, Tab } from '@mui/material';
import { useContext, useEffect } from 'react';
import { getViewDefintionFromObject } from '../../../../../../components/factories/AttributeDefinitions';
import { NodeEdgeMapperContext } from '../../../../../../ContextProviders/NodeEdgeMapperComponentContext';
import { DeviceControlDAO } from '../../../../../../data/DeviceControlDAO';
import { isAdminRole } from '../../../../../../helpers/access';
import { useTabHandlerStringKeys } from '../../../../../../hooks/useTabHandler';
import { batchGetLatestDCTs } from '../../EntityGroup/queries/batchGetLatestDCTs';
import { EntityGroupControlContext } from '../../EntityGroup/Reducers/EntityGroupControlReducer';
import { OnDemandProgrammeContext, } from '../Reducers/OnDemandProgrammeReducer';
import { WindowAllocation } from './Tabs/WindowAllocation/WindowAllocation';
import { ObjectDetailCard } from '../../../ObjectView/ObjectDetailCard';
const debug = process.env.REACT_APP_DEBUG && false;
export const OnDemandProgrammeInternal = (props) => {
    const { viewObject, transactionFunctions: TransactionFunctions, userFunctions: UserFunctions, userSelectedGroup, allowedActions, isSmallView } = props;
    const { matchedPrimary } = viewObject;
    const canaryString = matchedPrimary?.sk ?? 'OnDemandProgrammeInternal';
    const [state, dispatch] = useContext(OnDemandProgrammeContext);
    const { programme } = state;
    const { state: { nodeEdgeMapper, apexNode }, dispatch: nodeEdgeMapperDispatch, } = useContext(NodeEdgeMapperContext);
    const { dispatch: controlDispatch } = useContext(EntityGroupControlContext);
    const matchedPrimaryHash = getObjectHash(matchedPrimary);
    useEffect(() => {
        if (programme && !programme.supportedType) {
            console.log(`WARN: No supportedType on programme, redirecting to Details tab`);
        }
    }, [programme?.sk]);
    /** On change of programme this needs to clear all context contents before reconstituting everything  */
    useEffect(() => {
        if (matchedPrimary?.type === 'OnDemandProgramme' && isOnDemandProgrammeConstructorProps(matchedPrimary)) {
            /* if programme has changed or is an intial programme, SetProgramme, this will clear context and start again */
            if (matchedPrimary.sk !== programme?.sk || !programme) {
                const action = {
                    type: 'SetProgramme',
                    value: { programme: new OnDemandProgramme(matchedPrimary) },
                };
                dispatch(action);
            }
            else {
                const action = { type: 'MutateProgramme', value: { update: matchedPrimary } };
                dispatch(action);
            }
        }
        else {
            if (matchedPrimary) {
                console.log(`WARN: OnDemandProgrammeInternal not setting programme because`, matchedPrimary, isOnDemandProgrammeConstructorProps(matchedPrimary));
            }
        }
    }, [matchedPrimaryHash]);
    const viewDefinition = matchedPrimary ? getViewDefintionFromObject(matchedPrimary) : {};
    viewDefinition.supportedType = {
        key: 'supportedType',
        editable: true,
        label: 'Supported Type',
        defaultValue: matchedPrimary?.supportedType,
        type: AppValueTypes.ENUM,
        enumKV: Object.fromEntries(Object.keys(deviceEnabledTypes).map((k) => [k, k])),
        required: true,
    };
    const Entities = nodeEdgeMapper.getContainedItems().filter(isDeviceEnabledType);
    const entitiesHash = getObjectHash(Object.keys(Entities ?? {}));
    /* on change of entities, get clear entity control context */
    useEffect(() => {
        if (Entities && Object.keys(Entities ?? {}).length > 0) {
            const EntityGroupContext = new NodeEdgeMapper();
            EntityGroupContext.loadItems(Entities);
            const loadAction = { type: 'LoadContext', value: { EntityGroup: null, EntityGroupContext } };
            controlDispatch(loadAction);
            debug && console.log(`OnDemandProgrammeInternal batchGetLatestDCTs`, apexNode);
            batchGetLatestDCTs({
                Entities,
                addDCTs: (entity, dcts) => {
                    const action = { type: 'AddTransactions', value: { entity, dcts } };
                    controlDispatch(action);
                },
                broadcastScheduleSk: apexNode?.sk,
            });
        }
    }, [entitiesHash]);
    const tabDefinitions = {
        WindowAllocation: () => _jsx(WindowAllocation, {}),
        // CRA 2025 05 — disabled as it provides the user with options that they probably shouldn't be fiddling with,
        // as the DetailCard doesn't know the right APIs to use with a Programme
        Details: () => matchedPrimary ? (_jsx(ObjectDetailCard, { matchedPrimary: programme ?? matchedPrimary,
            viewDefinition,
            mostProximateCustomer: undefined,
            transactionFunctions: TransactionFunctions,
            userFunctions: UserFunctions,
            allowedActions })) : null,
    };
    const tabsToDisplay = tabDefinitions;
    const { state: [[tabValue]], handlers: { handleTabChange }, } = useTabHandlerStringKeys(tabsToDisplay);
    /** on change of programme  */
    useEffect(() => {
        if (programme) {
            const apexNodeAction = { type: 'setApexNode', value: { apexNode: programme } };
            nodeEdgeMapperDispatch(apexNodeAction);
            DeviceControlDAO.device.control.programme.ondemand.getContext({ programme }).then(({ err, data }) => {
                if (data) {
                    const { nodeEdgeMapper, apexNode } = data;
                    const action = { type: 'loadNEMContents', value: { canaryString, nodeEdgeMapper } };
                    nodeEdgeMapperDispatch(action);
                }
            });
        }
    }, [programme?.sk]);
    const viewSizeWrapper = (content) => {
        // unsetting overflow (on the container) is required to make `position: sticky` work
        return !isSmallView ? (_jsx(Card, { elevation: 3, sx: { overflow: 'unset' }, children: _jsx(CardContent, { sx: { padding: 0, ['&:last-child']: { padding: 2 } }, children: content }) })) : (content);
    };
    return isAdminRole(userSelectedGroup) ? (viewSizeWrapper(_jsxs(TabContext, { value: tabValue, children: [_jsx(TabList, { onChange: handleTabChange, children: Object.entries(tabsToDisplay)
                    .filter(([_, RFC], i) => RFC !== null)
                    .map(([tabName, _RFC], i) => (_jsx(Tab, { value: tabName, label: capitalize(camelToDisplay(tabName)), disabled: false }, `tabHeader_${i}`))) }), Object.entries(tabsToDisplay)
                .filter(([_k, c]) => !!c)
                .map(([k, component], index) => (_jsx(TabPanel, { value: k, sx: { padding: 1 }, children: component() }, `tabBase_${k}`)))] }))) : (_jsx(WindowAllocation, {}));
};
